import { z } from 'zod';
import { dateStringValidation } from './dateString.js';
import { dollarsToCents } from './dollarsToCents.js';
import { CONDITION_DETAILS, UNIT_FEATURE_DETAILS, UNIT_FEATURE_KEYS, UTILITY_DETAILS, UTILITY_KEYS, conditionRatingSchema, renovatedYearSchema, unitFeaturesSchema, unitUtilitiesSchema, } from './rentRadarFeatures.js';
export const rentIncreaseModeSchema = z.enum(['percent', 'amount']);
export const rentNoticeStatusSchema = z.enum(['draft', 'served', 'void']);
export const rentCapRuleSchema = z.enum(['fixed', 'ca_ab1482']);
export const AB1482_BASE_PCT = 5;
export const AB1482_CEILING_PCT = 10;
export function effectiveCapPct(settings) {
    switch (settings.capRule) {
        case 'fixed':
            return settings.maxIncreasePct;
        case 'ca_ab1482': {
            if (settings.ab1482Exempt)
                return null;
            if (settings.cpiPercent === null)
                return AB1482_CEILING_PCT;
            const cap = Math.round((AB1482_BASE_PCT + settings.cpiPercent) * 100) / 100;
            return Math.min(cap, AB1482_CEILING_PCT);
        }
        default: {
            const _exhaustive = settings.capRule;
            throw new Error(`Unhandled cap rule: ${String(_exhaustive)}`);
        }
    }
}
export function needsCpi(settings) {
    return settings.capRule === 'ca_ab1482' && !settings.ab1482Exempt && settings.cpiPercent === null;
}
export const estimateSourceSchema = z.object({ title: z.string(), url: z.string() });
export const ESTIMATE_CONFIDENCE_LOW_WIDTH = 0.25;
export const ESTIMATE_CONFIDENCE_HIGH_WIDTH = 0.12;
export const ESTIMATE_CONFIDENCE_HIGH_SOURCES = 3;
export const estimateConfidenceSchema = z.object({
    level: z.enum(['high', 'medium', 'low']),
    reason: z.string().min(1),
});
function widthPercent(width) {
    return `${Math.round(width * 100)}%`;
}
function rangeWiderThan(input, percent) {
    const estimateCents = Math.round(input.estimate * 100);
    const spanCents = Math.round(input.high * 100) - Math.round(input.low * 100);
    return spanCents * 100 > estimateCents * percent;
}
function sourceCountText(count) {
    return `${count} ${count === 1 ? 'source' : 'sources'}`;
}
export function estimateConfidence(input) {
    if (!input.grounded || input.sourceCount <= 0) {
        return { level: 'low', reason: 'Low confidence: no web sources' };
    }
    if (Math.round(input.estimate * 100) <= 0) {
        return { level: 'low', reason: 'Low confidence: the estimate is zero' };
    }
    const lowPercent = Math.round(ESTIMATE_CONFIDENCE_LOW_WIDTH * 100);
    const highPercent = Math.round(ESTIMATE_CONFIDENCE_HIGH_WIDTH * 100);
    if (rangeWiderThan(input, lowPercent)) {
        return {
            level: 'low',
            reason: `Low confidence: the range is wider than ${widthPercent(ESTIMATE_CONFIDENCE_LOW_WIDTH)} of the estimate`,
        };
    }
    if (input.sourceCount >= ESTIMATE_CONFIDENCE_HIGH_SOURCES &&
        !rangeWiderThan(input, highPercent)) {
        return { level: 'high', reason: 'High confidence' };
    }
    if (input.sourceCount < ESTIMATE_CONFIDENCE_HIGH_SOURCES) {
        return { level: 'medium', reason: `Medium confidence: ${sourceCountText(input.sourceCount)}` };
    }
    return {
        level: 'medium',
        reason: `Medium confidence: the range is wider than ${widthPercent(ESTIMATE_CONFIDENCE_HIGH_WIDTH)} of the estimate`,
    };
}
export function marketFigure(market) {
    if (market === null)
        return null;
    switch (market.kind) {
        case 'estimate':
            return market.estimate;
        case 'override':
            return market.amount;
        default: {
            const _exhaustive = market;
            throw new Error(`Unhandled market figure: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
function assertSafe(value) {
    if (!Number.isSafeInteger(value)) {
        throw new RangeError('Estimate arithmetic left the safe integer range');
    }
    return value;
}
export function adjustmentCents(values, key, defaultCents) {
    const stored = Object.hasOwn(values, key) ? values[key] : undefined;
    return stored !== undefined && Number.isSafeInteger(stored)
        ? { cents: stored, isDefault: false }
        : { cents: defaultCents, isDefault: true };
}
export function estimateBuildUp(input) {
    const { base, profile, values } = input;
    const baseCents = {
        estimate: dollarsToCents(base.estimate),
        low: dollarsToCents(base.low),
        high: dollarsToCents(base.high),
    };
    const lines = [];
    const rating = profile.condition;
    if (rating !== undefined && Object.hasOwn(CONDITION_DETAILS, rating)) {
        lines.push({
            kind: 'condition',
            rating,
            ...adjustmentCents(values, rating, CONDITION_DETAILS[rating].defaultCents),
        });
    }
    for (const key of UNIT_FEATURE_KEYS) {
        if (profile.features?.[key] !== true)
            continue;
        lines.push({
            kind: 'feature',
            key,
            ...adjustmentCents(values, key, UNIT_FEATURE_DETAILS[key].defaultCents),
        });
    }
    for (const key of UTILITY_KEYS) {
        if (profile.utilities?.[key] !== 'owner')
            continue;
        lines.push({
            kind: 'utility',
            key,
            ...adjustmentCents(values, key, UTILITY_DETAILS[key].defaultCents),
        });
    }
    const netCents = lines.reduce((sum, line) => assertSafe(sum + line.cents), 0);
    const moved = (cents) => assertSafe(cents + netCents);
    return {
        baseCents,
        lines,
        netCents,
        adjustedCents: {
            estimate: Math.max(0, moved(baseCents.estimate)),
            low: Math.max(0, moved(baseCents.low)),
            high: Math.max(0, moved(baseCents.high)),
        },
        estimateFloored: moved(baseCents.estimate) < 0,
    };
}
export function unitEstimateBuildUp(input) {
    const { base, profile, values, pricesPlainUnit } = input;
    return estimateBuildUp({ base, profile: pricesPlainUnit ? profile : {}, values });
}
export const rentNoticeServedMethodSchema = z.enum(['door', 'mail', 'email']);
export const ESTIMATE_PARAMETER_KEYS = [
    'location',
    'unitSize',
    'propertyAge',
    'buildingUpkeep',
    'poolRecRoom',
    'grounds',
    'seasonality',
];
export const estimateParametersSchema = z.object({
    location: z.boolean(),
    unitSize: z.boolean(),
    propertyAge: z.boolean(),
    buildingUpkeep: z.boolean(),
    poolRecRoom: z.boolean(),
    grounds: z.boolean(),
    seasonality: z.boolean(),
});
export const DEFAULT_ESTIMATE_PARAMETERS = {
    location: true,
    unitSize: true,
    propertyAge: true,
    buildingUpkeep: true,
    poolRecRoom: true,
    grounds: true,
    seasonality: false,
};
const oneToFiveSchema = z.number().int().min(1).max(5);
export const propertyRentProfileSchema = z.object({
    upkeep: oneToFiveSchema.optional(),
    recRoom: z.boolean().optional(),
    groundsKept: z.boolean().optional(),
});
export const unitRentProfileSchema = z.object({
    condition: conditionRatingSchema.optional(),
    renovatedYear: renovatedYearSchema.optional(),
    features: unitFeaturesSchema.optional(),
    utilities: unitUtilitiesSchema.optional(),
});
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
export function readUnitRentProfile(stored) {
    const parsed = unitRentProfileSchema.safeParse(stored);
    if (parsed.success)
        return { profile: parsed.data, issues: [] };
    const { issues } = parsed.error;
    if (!isPlainObject(stored))
        return { profile: {}, issues };
    const kept = Object.fromEntries(Object.entries(stored).map(([field, value]) => [
        field,
        isPlainObject(value) ? { ...value } : value,
    ]));
    for (const issue of issues) {
        const [field, entry] = issue.path.map(String);
        if (field === undefined)
            continue;
        const value = kept[field];
        if (entry !== undefined && isPlainObject(value))
            delete value[entry];
        else
            delete kept[field];
    }
    const retry = unitRentProfileSchema.safeParse(kept);
    return { profile: retry.success ? retry.data : {}, issues };
}
export const ESTIMATE_NOTE_MAX_LENGTH = 2000;
export const DEFAULT_RENT_RADAR_SETTINGS = {
    increaseMode: 'percent',
    increaseValue: 5,
    noticeLeadDays: 60,
    maxIncreasePct: 10,
    estimateParameters: DEFAULT_ESTIMATE_PARAMETERS,
    propertyProfile: {},
    adjustmentValues: {},
    capRule: 'fixed',
    cpiPercent: null,
    cpiLabel: null,
    ab1482Exempt: false,
};
const earlierTimeoutAt = z.string().datetime().optional();
export const noticeEmailDeliverySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('disabled'),
        at: z.string().datetime(),
        to: z.string(),
        earlierTimeoutAt,
    }),
    z.object({ kind: z.literal('recorded'), at: z.string().datetime(), to: z.string() }),
    z.object({
        kind: z.literal('sending'),
        at: z.string().datetime(),
        to: z.string(),
        earlierTimeoutAt,
    }),
    z.object({ kind: z.literal('sent'), at: z.string().datetime(), to: z.string() }),
    z.object({
        kind: z.literal('failed'),
        at: z.string().datetime(),
        to: z.string().nullable(),
        reason: z.string().min(1),
        earlierTimeoutAt,
    }),
]);
export const NOTICE_EMAIL_RETRY_AFTER_MS = 10 * 60 * 1000;
export const NOTICE_EMAIL_TIMEOUT_REASON = 'timeout';
export function earlierTimeout(delivery) {
    if (delivery === null)
        return undefined;
    switch (delivery.kind) {
        case 'failed':
            return delivery.reason === NOTICE_EMAIL_TIMEOUT_REASON
                ? delivery.at
                : delivery.earlierTimeoutAt;
        case 'sending':
            return delivery.earlierTimeoutAt ?? delivery.at;
        case 'disabled':
            return delivery.earlierTimeoutAt;
        case 'recorded':
        case 'sent':
            return undefined;
        default: {
            const _exhaustive = delivery;
            throw new Error(`Unhandled email delivery: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export function draftWasEmailed(delivery) {
    if (delivery === null)
        return false;
    switch (delivery.kind) {
        case 'sent':
        case 'sending':
        case 'recorded':
            return true;
        case 'failed':
        case 'disabled':
            return earlierTimeout(delivery) !== undefined;
        default: {
            const _exhaustive = delivery;
            throw new Error(`Unhandled email delivery: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export function noticeEmailGate(delivery, now) {
    const afterWindow = (attempt, at) => now.getTime() - Date.parse(at) >= NOTICE_EMAIL_RETRY_AFTER_MS
        ? { kind: 'retry' }
        : { kind: 'wait', attempt };
    if (delivery === null)
        return { kind: 'open' };
    switch (delivery.kind) {
        case 'sent':
        case 'recorded':
            return { kind: 'sent', to: delivery.to };
        case 'sending':
            return afterWindow('sending', delivery.at);
        case 'failed':
            return delivery.reason === NOTICE_EMAIL_TIMEOUT_REASON
                ? afterWindow('timeout', delivery.at)
                : { kind: 'open' };
        case 'disabled':
            return { kind: 'open' };
        default: {
            const _exhaustive = delivery;
            throw new Error(`Unhandled email delivery: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export const NOTICE_EMAIL_NO_EMAIL_REASON = 'no_email';
export const rentNoticeLetterPartiesSchema = z.object({
    ownershipName: z.string(),
    unitAddressLines: z.array(z.string()),
    preparedByName: z.string(),
    letterDate: dateStringValidation,
    timeZone: z.string(),
});
export const rentNoticeTenantSchema = z.object({
    unitPartyId: z.number().int(),
    name: z.string(),
});
//# sourceMappingURL=rentRadar.js.map